<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-pb-0">
        <div class="tw-flex tw-items-center" v-if="isLoading">
          <base-skeleton-loader class="tw-h-16 tw-w-16 md:tw-h-20 md:tw-w-20 tw-flex-shrink-0 tw-rounded-full" />
          <div class="tw-ml-4 tw-mb-px sm:tw-mb-0">
            <base-skeleton-loader class="tw-h-4 tw-w-48 tw-mt-2 sm:tw-mt-2.5 md:tw-mt-3" />
            <div class="tw-flex">
              <base-skeleton-loader class="tw-h-3 tw-mt-1 sm:tw-mt-2.5 md:tw-mt-3 tw-w-32 tw-mr-5" v-for="i in organization.collectDobSetting ? 3 : 2" :key="i" />
            </div>
          </div>
        </div>
        <div class="tw-flex tw-items-center tw-justify-between" v-else>
          <div class="tw-flex tw-items-center">
            <div class="tw-h-16 tw-w-16 md:tw-h-20 md:tw-w-20 tw-flex-shrink-0">
              <Avatar :user-id="userId" avatar-type="user" />
            </div>
            <div class="tw-ml-4">
              <h1 class="tw-text-xl sm:tw-text-2xl md:tw-text-3xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">{{ member.fullName }}</h1>
              <div class="tw-text-gray-500 tw-text-xs sm:tw-text-sm tw-flex tw-items-center tw-flex-wrap">
                <div class="tw-flex tw-items-center tw-mr-5">
                  <mail-icon class="tw-h-3.5 tw-w-3.5 tw-mr-1.5 tw-flex-shrink-0" />
                  <a class="hover:tw-text-primary tw-text-gray-500" :href="`mailto:${member.email}`">{{ member.email }}</a>
                </div>
                <div class="tw-flex tw-items-center tw-mr-5" v-if="member.phone">
                  <phone-icon class="tw-h-3.5 tw-w-3.5 tw-mr-1.5 tw-flex-shrink-0" />
                  <base-phone-number-field :value="member.phone" />
                </div>
                <div class="tw-flex tw-items-center" v-if="member.dob && organization.collectDobSetting">
                  <birthday-icon class="tw-h-3.5 tw-w-3.5 tw-mr-1.5 tw-flex-shrink-0" />
                  {{ formatDate(member.dob) }}
                </div>
              </div>
              <div class="tw-flex tw-items-center tw-flex-wrap tw-gap-2 tw-mt-1.5">
                <base-badge v-for="plan in member.activePlans" :key="plan.PlanName" color="gray" size="sm">{{ plan.planName }}: {{ plan.locationName }}</base-badge>
              </div>
            </div>
          </div>
          <base-dropdown :show-background="false" align="right" :options="dropdownOptions" width="52">
            <base-icon>
              <more-vertical-icon class="tw-w-6 tw-h-6" />
            </base-icon>
          </base-dropdown>
        </div>
        <base-tabs class="tw-mt-2 tw-pb-4 md:tw-pb-0 tw-mb-0" no-border @click="tabClick($event)" :key="`${tabs.length}-${userId}`">
          <base-tab :title="tab.title" :selected="currentComponent === tab.component" v-for="tab in tabs" :key="tab.title" />
        </base-tabs>
      </base-content>
    </div>
    <component :is="currentComponentWrapper || 'base-content'" :key="userId">
      <keep-alive>
        <component
          :is="currentComponent"
          :is-loading="isLoading"
          :member="member"
          :business-id="businessId"
          :user-id="userId"
          :member-full-name="member.fullName"
          @refresh="loadMember"
          @updated="member = $event"
        ></component>
      </keep-alive>
    </component>
    <send-password-reset-email-modal ref="sendPasswordResetEmailModal" :business-id="businessId" :user-id="userId" />
    <reset-password-modal ref="resetPasswordModal" :business-id="businessId" :user-id="userId" />
    <invite-to-plan-slide-over ref="inviteToPlanSlideOver" :business-id="businessId" :user-id="userId" :email="member.email" :phone="member.phone" :full-name="member.fullName" />
    <merge-users-slide-over ref="mergeUsersSlideOver" :business-id="businessId" :user-id="userId" />
    <new-rx-slide-over ref="newRxSlideOver" :business-id="businessId" :user-id="userId" @created="handleNewRx" />
    <add-business-member-plan ref="newPlanSlideOver" :business-id="businessId" :user-id="userId" @created="handleNewPlan" />
  </div>
</template>

<script>
import Avatar from "@/components/Avatar";
import SendPasswordResetEmailModal from "@/views/business/member/components/SendPasswordResetEmailModal";
import ResetPasswordModal from "@/views/business/member/components/ResetPasswordModal";
import InviteToPlanSlideOver from "@/views/business/member/components/InviteToPlanSlideOver";
import MergeUsersSlideOver from "@/views/business/member/components/MergeUsersSlideOver";
import NewRxSlideOver from "@/views/business/member/components/NewRxSlideOver";
import AddBusinessMemberPlan from "@/views/business/member/components/AddBusinessMemberPlan";
import { PhoneIcon, MailIcon, MoreVerticalIcon, BirthdayIcon } from "@/assets/svgs";
import { mapGetters } from "vuex";
import { capitalize } from "lodash";
import moment from "moment";

export default {
  components: {
    Avatar,
    SendPasswordResetEmailModal,
    ResetPasswordModal,
    InviteToPlanSlideOver,
    MergeUsersSlideOver,
    NewRxSlideOver,
    AddBusinessMemberPlan,
    PhoneIcon,
    MailIcon,
    MoreVerticalIcon,
    BirthdayIcon,
    BusinessMemberInfo: () => import("@/views/business/member/components/BusinessMemberInfo"),
    BusinessMemberPlans: () => import("@/views/business/member/plans/BusinessMemberPlans.vue"),
    BusinessMemberHistory: () => import("@/views/business/member/history/BusinessMemberHistory.vue"),
    BusinessMemberPaymentMethod: () => import("@/views/business/member/components/BusinessMemberPaymentMethod.vue"),
    BusinessMemberInvites: () => import("@/views/business/member/components/BusinessMemberInvites.vue"),
    BusinessMemberPrescriptions: () => import("@/views/business/member/prescriptions/BusinessMemberPrescriptions.vue"),
    BusinessMemberDocuments: () => import("@/views/business/member/components/BusinessMemberDocuments.vue"),
    BusinessMemberTodo: () => import("@/views/business/todo/member-todo/BusinessMemberTodo"),
    LocationMemberTodo: () => import("@/views/business/todo/member-todo/LocationMemberTodo"),
    BusinessMemberShippedHistoryList: () => import("@/views/business/member/components/BusinessMemberShippedHistoryList"),
    BusinessMemberTrackingList: () => import("@/views/business/member/components/BusinessMemberTrackingList"),
  },
  props: {
    businessId: String,
    userId: String,
  },
  data() {
    return {
      isLoading: false,
      member: {},
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("role", ["isLocationFiltered", "isBusinessAdministrator", "isPscSecondaryRole", "isBusinessManagement"]),
    ...mapGetters("cognito", { userName: "name", userEmail: "email" }),

    showAddNewPlan() {
      if (this.isPscSecondaryRole) {
        return false; // hide Add New Plan if has PSC secondary role
      }
      return !(this.organization.operatingModel === "coordinator" && this.isLocationFiltered);
    },
    dropdownOptions() {
      return [
        ...(this.organization.operatingModel === "direct" ? [{ label: "Invite to Plan", onClick: () => this.$refs.inviteToPlanSlideOver.show() }] : []),
        ...(this.showAddNewPlan
          ? [
              {
                label: "Add New Plan",
                onClick: () => this.addMemberPlan(),
              },
            ]
          : []),
        ...(!this.isPscSecondaryRole && this.organization.usePrescriptionProgramSetting ? [{ label: "Add New Program", onClick: () => this.$refs.newRxSlideOver.show() }] : []),
        { label: "Send Password Reset Email", onClick: () => this.$refs.sendPasswordResetEmailModal.show() },
        ...(this.isBusinessManagement || (this.isLocationFiltered == false && this.isBusinessAdministrator(this.businessId))
          ? [{ label: "Reset Password", onClick: () => this.$refs.resetPasswordModal.show() }]
          : []),
        ...(this.isLocationFiltered == false && this.isBusinessAdministrator(this.businessId)
          ? [
              {
                label: "Merge User",
                onClick: () =>
                  this.$refs.mergeUsersSlideOver.show({
                    userId: this.userId,
                    fullName: this.member.fullName,
                  }),
              },
            ]
          : []),
      ];
    },
    tabs() {
      const baseTabs = [
        { title: `${capitalize(this.organization.customerLabelSingular)} Info`, component: "BusinessMemberInfo", route: "personal-info" },
        { title: "Plans", component: "BusinessMemberPlans", route: "plans" },
        ...(this.organization.usePrescriptionProgramSetting ? [{ title: "Prescriptions", component: "BusinessMemberPrescriptions", route: "prescriptions" }] : []),
        { title: "Payment Methods", component: "BusinessMemberPaymentMethod", route: "paymentmethod" },
        { title: "History", component: "BusinessMemberHistory", route: "history" },
        { title: "Documents", component: "BusinessMemberDocuments", route: "documents" },
        ...(this.organization.useTodoSetting && this.isLocationFiltered === false
          ? [{ title: "Activities", component: "BusinessMemberTodo", route: "business-todo", wrapper: "div" }]
          : []),
        ...(this.organization.useTodoSetting && this.isLocationFiltered === true
          ? [{ title: "Activities", component: "LocationMemberTodo", route: "location-todo", wrapper: "div" }]
          : []),
        { title: "Order Tracking", component: "BusinessMemberTrackingList", route: "order-tracking" },
        { title: "Shipment History", component: "BusinessMemberShippedHistoryList", route: "order-history" },
      ];
      return baseTabs;
    },
    currentComponent() {
      return this.$route.meta.component;
    },
    currentComponentWrapper() {
      const tab = this.tabs.find((t) => t.component === this.currentComponent);
      return tab ? tab.wrapper : null;
    },
  },
  methods: {
    href(path) {
      if (this.$route.path !== path) {
        this.$router.push({
          path,
        });
      }
    },
    tabClick(tabTitle) {
      const tab = this.tabs.find((t) => t.title === tabTitle);
      this.href(`/businesses/${this.businessId}/members/${this.userId}/${tab.route}`);
    },
    addMemberPlan() {
      this.$refs.newPlanSlideOver.show();
    },
    async loadMember() {
      try {
        this.isLoading = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/members/${this.userId}`);
        this.member = response.data;
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: `Unable to load ${capitalize(this.organization.customerLabelSingular)} details`,
          message: `We were not able to retrieve the ${capitalize(
            this.organization.customerLabelSingular,
          )}'s data. There may have been a connection error. Please reload the page to try again.`,
          error,
        });
      } finally {
        this.isLoading = false;
      }
    },
    handleNewRx() {
      this.EventBus.$emit("prescription-created");
      this.href(`/businesses/${this.businessId}/members/${this.userId}/prescriptions`);
    },
    handleNewPlan() {
      this.EventBus.$emit("member_update");
      this.href(`/businesses/${this.businessId}/members/${this.userId}/plans`);
    },
    formatDate(date) {
      return moment(date).format("M/D/YYYY");
    },
  },
  created() {
    this.loadMember();
  },
  watch: {
    userId() {
      this.loadMember();
    },
  },
};
</script>
